/**
 * Provenance of a reservation's first confirmation, carried by the
 * reservation document itself (`publicLifecycle`), so every trigger execution
 * can tell from its own before/after snapshots, in any order, whether its
 * write was the booking's original first confirmation. Shared by every
 * writer of `requestsV03` (API server, Cloud Functions, dashboard).
 *
 * Writer contract:
 * 1. The write that leaves a reservation confirmed while the stored document
 *    was neither confirmed nor carries a confirmation history sets
 *    `firstConfirmedAt` (server timestamp) in the SAME write.
 * 2. `publicLifecycle` is immutable afterwards: updates never touch it; full
 *    writes carry the STORED value over whatever the written copy holds.
 * 3. Recreating a deleted reservation under the SAME id from its copy sets
 *    `restoredAt` and keeps the copy's history (`restoredDocument`). An
 *    unstamped copy that was confirmed, or may have been (cancelled), keeps
 *    an honest `unstampedHistory` mark instead of an invented time; only a
 *    copy that ended unconfirmed (a hold, a request) can be first confirmed
 *    by its restore (`firstConfirmedOnRestore`).
 * 4. A NEW id built from another booking (regular table, template, clone)
 *    drops the source's lifecycle and Public API context (`forNewReservation`).
 *
 * A booking confirmed by a writer from before this contract, and still
 * unstamped, is never stamped later: its first confirmation time is unknown,
 * and a later write must not claim one.
 *
 * Pure data and functions only: this file is copied into the dashboard. The
 * server timestamp sentinel comes from the caller's own Firestore SDK.
 */

export const PUBLIC_LIFECYCLE_FIELD = 'publicLifecycle';
export const FIRST_CONFIRMED_AT_PATH = 'publicLifecycle.firstConfirmedAt';
export const RESTORED_AT_PATH = 'publicLifecycle.restoredAt';

/** Guest name of a widget booking still being created (never a booking of its own). */
export const PRELIMINARY_GUEST_NAME = 'Wird gerade erstellt...';

export type NativeTime = { seconds: number; nanoseconds: number };

export type PublicLifecycle<T = NativeTime> = {
  /** Set once, by the write that first confirmed the booking. */
  firstConfirmedAt?: T;
  /** Set by every same-id restore of a deleted booking. */
  restoredAt?: T;
  /** The restore itself was the first confirmation (copy ended unconfirmed). */
  firstConfirmedOnRestore?: boolean;
  /** Confirmed (or possibly confirmed) before writers stamped; time unknown. */
  unstampedHistory?: boolean;
  /**
   * A booking with a confirmation history was restored without being
   * confirmed (as a hold or failed): true until the write that confirms it
   * again, which sets it to false and is the booking's return.
   */
  awaitsReconfirmation?: boolean;
  /**
   * Set with `firstConfirmedAt` when that stamp was written reactivating an
   * unstamped cancellation: whether the booking was confirmed before writers
   * stamped is unknown (readers decide by the document's age against the
   * site's `firstConfirmationStampsSince`); the time is the first stamp, not
   * a known first confirmation.
   */
  priorHistoryUnknown?: boolean;
};

type Data = Record<string, any> | undefined | null;

export type ReservationPhase = 'absent' | 'excluded' | 'unconfirmed' | 'confirmed' | 'cancelled';

/** Blocks, test and preliminary bookings are no bookings of their own for integrations. */
export function isExportableReservation(reservation: Data) {
  if (!reservation) return false;
  if (reservation.blocked || reservation.blockedFullShift) return false;
  if (reservation.isTest) return false;
  if (reservation.preliminary) return false;
  if (reservation.guest?.name === PRELIMINARY_GUEST_NAME) return false;
  return true;
}

/** The business phase of a stored reservation, as webhooks see it. */
export function reservationPhase(data: Data): ReservationPhase {
  if (!data) return 'absent';
  if (!isExportableReservation(data)) return 'excluded';
  if (data.status === 'failed' || data.status === 'negative') return data.noShow ? 'confirmed' : 'cancelled';
  // Payment/card holds and bookings still awaiting the restaurant's approval.
  if (data.validTill || data.noShowConfirmation?.validTill) return 'unconfirmed';
  return data.status === 'success' ? 'confirmed' : 'unconfirmed';
}

/**
 * Fields whose change can move a reservation into or out of the confirmed
 * phase (of the guest only the name: the placeholder of a form-stage hold).
 */
export const PHASE_FIELDS = [
  'status',
  'noShow',
  'validTill',
  'noShowConfirmation',
  'blocked',
  'blockedFullShift',
  'isTest',
  'preliminary',
  'guest.name',
] as const;

const topOf = (path: string) => path.split('.')[0];

/** Whether a field patch (top-level or dotted paths) can change the phase. */
export function touchesPhase(patch: Record<string, unknown>) {
  return Object.keys(patch).some((path) =>
    (PHASE_FIELDS as readonly string[]).some(
      (field) => path === field || path.startsWith(`${field}.`) || field.startsWith(`${path}.`)
    )
  );
}

/** Whether a field patch writes the lifecycle itself. */
export function touchesLifecycle(patch: Record<string, unknown>) {
  return Object.keys(patch).some((path) => topOf(path) === PUBLIC_LIFECYCLE_FIELD);
}

function timeOf(value: any): NativeTime | null {
  if (!value || typeof value !== 'object') return null;
  // Native SDK timestamps, and their JSON form on either SDK.
  const seconds = value.seconds ?? value._seconds;
  const nanoseconds = value.nanoseconds ?? value._nanoseconds;
  return typeof seconds === 'number' && typeof nanoseconds === 'number' ? { seconds, nanoseconds } : null;
}

export function firstConfirmedAtOf(data: Data): NativeTime | null {
  return timeOf(data?.publicLifecycle?.firstConfirmedAt);
}

/** Milliseconds of the first confirmation, or null when unknown. */
export function firstConfirmedMillis(data: Data) {
  const stamp = firstConfirmedAtOf(data);
  return stamp ? stamp.seconds * 1000 + Math.floor(stamp.nanoseconds / 1_000_000) : null;
}

/** Confirmed before: stamped, or marked as confirmed before writers stamped. */
export function hasConfirmationHistory(data: Data) {
  return !!firstConfirmedAtOf(data) || data?.publicLifecycle?.unstampedHistory === true;
}

/** A write from `current` to `next` is the first confirmation and must carry the stamp. */
function firstConfirms(current: Data, next: Data) {
  return (
    !hasConfirmationHistory(current) &&
    // Confirmed by a writer from before the contract: the time is unknown.
    reservationPhase(current) !== 'confirmed' &&
    reservationPhase(next) === 'confirmed'
  );
}

/** Method name of a FieldValue sentinel (Admin SDK, client SDK, compat wrapper), else null. */
function sentinelMethod(value: any): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const name = value.methodName ?? value._methodName ?? value._delegate?._methodName;
  if (typeof name === 'string') return name;
  return /Transform$|^FieldValue$/.test(value.constructor?.name ?? '') ? String(value.constructor.name) : null;
}

const isSentinel = (value: any) => sentinelMethod(value) !== null;

const isDeleteSentinel = (value: any) => /delete/i.test(sentinelMethod(value) ?? '');

/**
 * `current` with a field patch applied, for judging the phase. Delete
 * sentinels remove the field; other transforms keep the stored value.
 */
export function withFieldPatch(current: Data, patch: Record<string, any>): Record<string, any> {
  const next: Record<string, any> = { ...(current ?? {}) };
  Object.entries(patch).forEach(([path, value]) => {
    const keys = path.split('.');
    let target = next;
    keys.slice(0, -1).forEach((key) => {
      target[key] = target[key] && typeof target[key] === 'object' && !isSentinel(target[key]) ? { ...target[key] } : {};
      target = target[key];
    });
    const last = keys[keys.length - 1];
    if (value === undefined || isDeleteSentinel(value)) delete target[last];
    else if (!isSentinel(value)) target[last] = value;
  });
  return next;
}

/** A patch or document without any lifecycle field: only the helpers below write it. */
export function withoutLifecycle<P extends Record<string, any>>(data: P): P {
  return Object.fromEntries(Object.entries(data).filter(([path]) => topOf(path) !== PUBLIC_LIFECYCLE_FIELD)) as P;
}

/** A first stamp that reactivates an unstamped cancellation of unknown history. */
function afterUnknownHistory(current: Data) {
  return reservationPhase(current) === 'cancelled' && !endedUnconfirmed(current);
}

/** A restored booking with history becomes confirmed again with this write. */
function reconfirms(current: Data, next: Data) {
  return (
    current?.[PUBLIC_LIFECYCLE_FIELD]?.awaitsReconfirmation === true &&
    reservationPhase(current) !== 'confirmed' &&
    reservationPhase(next) === 'confirmed'
  );
}

/**
 * For `update()` writers: the patch to write onto `current` (the stored
 * state read in the same transaction), stamped when it first confirms.
 */
export function lifecycleUpdate<T, P extends Record<string, any>>(
  current: Data,
  patch: P,
  serverTimestamp: () => T
): P & Record<string, any> {
  const clean: Record<string, any> = withoutLifecycle(patch);
  const next = withFieldPatch(current, clean);
  if (firstConfirms(current, next)) {
    clean[FIRST_CONFIRMED_AT_PATH] = serverTimestamp();
    if (afterUnknownHistory(current)) clean[`${PUBLIC_LIFECYCLE_FIELD}.priorHistoryUnknown`] = true;
  }
  if (reconfirms(current, next)) clean[`${PUBLIC_LIFECYCLE_FIELD}.awaitsReconfirmation`] = false;
  return clean as P & Record<string, any>;
}

/**
 * For `set()` writers, creating or overwriting a document at its own id:
 * the document to write. The stored lifecycle is carried over (a copy's
 * never replaces it), stamped when the write first confirms.
 */
export function lifecycleDocument<T>(current: Data, next: Record<string, any>, serverTimestamp: () => T) {
  const clean = withoutLifecycle(next);
  let lifecycle: Record<string, any> | undefined = current?.[PUBLIC_LIFECYCLE_FIELD] ?? undefined;
  if (firstConfirms(current, clean)) {
    lifecycle = {
      ...(lifecycle ?? {}),
      firstConfirmedAt: serverTimestamp(),
      ...(afterUnknownHistory(current) ? { priorHistoryUnknown: true } : {}),
    };
  }
  if (reconfirms(current, clean)) lifecycle = { ...lifecycle, awaitsReconfirmation: false };
  return lifecycle ? { ...clean, [PUBLIC_LIFECYCLE_FIELD]: lifecycle } : clean;
}

/**
 * A cancelled reservation that provably ended before it was ever confirmed:
 * still a payment or card hold when it failed (confirming clears those), or
 * failed because its payment was abandoned. Any other cancellation of an
 * unstamped booking may have followed a confirmation.
 */
export function endedUnconfirmed(data: Data) {
  return (
    reservationPhase(data) === 'cancelled' &&
    (!!data?.validTill || !!data?.noShowConfirmation?.validTill || data?.cancelationType === 'payment_abandoned')
  );
}

/** An unstamped copy whose booking may have been confirmed before writers stamped. */
function mayHaveBeenConfirmed(copy: Data) {
  const phase = reservationPhase(copy);
  if (phase === 'confirmed') return true;
  return phase === 'cancelled' && !endedUnconfirmed(copy);
}

/**
 * For a writer recreating a deleted reservation under the SAME id: the
 * document to write, `next` built from the deleted `copy` (the copy as
 * stored, with its lifecycle). Every restore is marked; the restore is the
 * first confirmation only when the copy never was confirmed.
 */
export function restoredDocument<T>(copy: Data, next: Record<string, any>, serverTimestamp: () => T) {
  const clean = withoutLifecycle(next);
  const source: Record<string, any> = copy?.[PUBLIC_LIFECYCLE_FIELD] ?? {};
  // Back without being confirmed (a hold until paid, or failed until recovered).
  const awaitsReconfirmation = reservationPhase(clean) !== 'confirmed';
  let lifecycle: Record<string, any>;
  if (hasConfirmationHistory(copy)) {
    lifecycle = {
      ...(source.firstConfirmedAt ? { firstConfirmedAt: source.firstConfirmedAt } : {}),
      ...(source.priorHistoryUnknown ? { priorHistoryUnknown: true } : {}),
      ...(source.unstampedHistory ? { unstampedHistory: true } : {}),
      restoredAt: serverTimestamp(),
      awaitsReconfirmation,
    };
  } else if (mayHaveBeenConfirmed(copy)) {
    lifecycle = { unstampedHistory: true, restoredAt: serverTimestamp(), awaitsReconfirmation };
  } else if (!awaitsReconfirmation) {
    lifecycle = { firstConfirmedAt: serverTimestamp(), firstConfirmedOnRestore: true, restoredAt: serverTimestamp() };
  } else {
    lifecycle = { restoredAt: serverTimestamp() };
  }
  return { ...clean, [PUBLIC_LIFECYCLE_FIELD]: lifecycle };
}

/** Fields that belong to one booking id and must not follow a copy to a new id. */
const ID_BOUND_FIELDS = [PUBLIC_LIFECYCLE_FIELD, 'publicApiCreatedBy', 'publicApiExternalReference', 'publicApiQuotaCountedAt'];

/**
 * For a writer creating a NEW reservation id from an existing document
 * (regular table, template, duplicate): the copy without the source's
 * lifecycle and Public API context, so the new booking gets its own first
 * confirmation and is not mistaken for the source's API operation.
 */
export function forNewReservation<D extends Record<string, any>>(copy: D): D {
  return Object.fromEntries(Object.entries(copy).filter(([key]) => !ID_BOUND_FIELDS.includes(key))) as D;
}

export type ConfirmationProvenance = 'this_write' | 'earlier_write' | 'unknown';

type SnapshotLike = { exists: boolean; data: () => Data };

/**
 * Reader side, from a trigger's own before/after snapshots: 'this_write' when
 * this write first confirmed the booking, 'earlier_write' when it was
 * confirmed before (stamp or history already there, or a restore of a once
 * confirmed copy), 'unknown' for documents without lifecycle.
 */
export function confirmationProvenance(before: SnapshotLike, after: SnapshotLike): ConfirmationProvenance {
  const afterData = after.exists ? after.data() : undefined;
  if (!hasConfirmationHistory(afterData)) return 'unknown';
  if (!firstConfirmedAtOf(afterData)) return 'earlier_write';
  if (before.exists) return hasConfirmationHistory(before.data()) ? 'earlier_write' : 'this_write';
  const lifecycle = afterData?.publicLifecycle ?? {};
  return lifecycle.restoredAt && !lifecycle.firstConfirmedOnRestore ? 'earlier_write' : 'this_write';
}

/** A document written back under its own id after a delete. */
export function isRestoredCreate(before: SnapshotLike | null, after: SnapshotLike) {
  return !before?.exists && after.exists && !!after.data()?.publicLifecycle?.restoredAt;
}
